import { Box, Card, Stack } from '@mui/material';
import { useMemo, useState } from 'react';

import { SectionTitle } from '~/components/common/SectionTitle';
import { ElevatorIdentityFields } from '~/components/compliance/detailPage/ElevatorIdentityFields';
import { ElevatorKpiFields } from '~/components/compliance/detailPage/ElevatorKpiFields';
import MonthRangePicker from '~/components/date/MonthRangePicker/MonthRangePicker';
import type { SelectedDateRange } from '~/components/date/MonthRangePicker/types';
import { useGlobalFilterContext } from '~/contexts/globalFilterContext';
import { useContractsForProperty } from '~/data/hooks/useContracts';
import type { API_Compliance } from '~/data/schemas/compliance';
import { usePropertyCarsKpis } from '~/hooks/usePropertyCarsKpis';
import { resolveOnTimeTarget } from '~/lib/kpi/contractTargets';
import { selectGoverningContractForCar } from '~/lib/kpi/governingContract';

/**
 * Service & Performance: the property Elevators grid's ticket-derived columns for this
 * one car, which are otherwise ~28 columns of sideways scrolling away.
 *
 * The tab owns its own read. The KPIs are the one thing on this page that is of a
 * period rather than of today, and a detail page carries no global filter band — so the
 * picker sits on the card whose numbers it moves, in local state, and narrowing it here
 * never rewrites the global selection.
 */
export function ElevatorMaintenanceTab({ compliance }: { compliance: API_Compliance }) {
  // Opens on whatever the rest of the app is scoped to, so the figures match the
  // property Elevators row this car was most likely opened from.
  const {
    selectedFilters: { date },
  } = useGlobalFilterContext();
  const [period, setPeriod] = useState<{ start: Date; end: Date }>({
    start: date.start,
    end: date.end,
  });

  const { cars, loading } = usePropertyCarsKpis(compliance.propertyId, { dateRange: period });
  const kpis = useMemo(
    () => cars?.find((row) => row.id === compliance.carId) ?? null,
    [cars, compliance.carId],
  );

  // The on-time response target is the one KPI target the car payload does not carry —
  // it lives on the contract — so it is read from the contract covering this unit. Same
  // request the Contracts tab makes, under the same SWR key.
  const { contracts } = useContractsForProperty(compliance.propertyId);
  const onTimeTarget = useMemo(
    () =>
      resolveOnTimeTarget(
        selectGoverningContractForCar(contracts, compliance.propertyId, compliance.carId)
          ?.onTimeResponseTimeTargetPct,
      ),
    [contracts, compliance.propertyId, compliance.carId],
  );

  // The picker reports a cleared range while a selection is half-made; the KPIs
  // have to be of *some* period, so hold the last complete one.
  const onPeriodChange = (next: SelectedDateRange) => {
    if (next.start && next.end) {
      setPeriod({ start: next.start, end: next.end });
    }
  };

  return (
    <Box sx={{ p: 3, width: '100%' }}>
      <Stack gap={3}>
        <Card variant="outlined" sx={{ p: 2 }}>
          <SectionTitle>Service</SectionTitle>
          <Box mt={2}>
            <ElevatorIdentityFields compliance={compliance} kpis={kpis} kpisLoading={loading} />
          </Box>
        </Card>

        <Card variant="outlined" sx={{ p: 2 }}>
          <Stack direction="row" alignItems="center" gap={2} flexWrap="wrap">
            <SectionTitle>Performance</SectionTitle>
            <MonthRangePicker placeHolder="Period" value={period} onSelect={onPeriodChange} />
          </Stack>
          <Box mt={2}>
            <ElevatorKpiFields
              kpis={kpis}
              propertyId={compliance.propertyId}
              loading={loading}
              onTimeTarget={onTimeTarget}
            />
          </Box>
        </Card>
      </Stack>
    </Box>
  );
}
